import type { FunctionComponent } from 'react'
import React from 'react'

import classnames from 'classnames'

import { AnimationWrapper } from '../../AnimationWrapper'
import type { BadgeProps } from '../../Badge'
import { Badge } from '../../Badge'
import { BaseLink } from '../../Links'
import type { PictureProps } from '../../Picture'
import { Picture } from '../../Picture'
import { DateTime } from '../../Seatbelt2/BuildingBlocks/DateTime/DateTime'
import { TypographyV2 } from '../../Seatbelt2/Foundations/Typography'
import styles from './FeaturedArticleCard.module.scss'

export const FeaturedArticleCard: FunctionComponent<
  FeaturedArticleCardProps
> = ({ article, alignLeft, badge, className, linkProps }) => {
  const { href, title, standfirst, date, image, isLive = false } = article

  return (
    <article
      className={classnames(
        styles.featuredArticleCard,
        badge && styles.featuredArticleCardWithBadge,
        alignLeft && styles.featuredArticleCardAlignLeft,
        className
      )}
    >
      <BaseLink href={href} invertedUnderline={true} {...linkProps}>
        <Badge className={styles.featuredArticleCardBadge} {...badge} />
        <div className={styles.featuredArticleCardContent}>
          {image ? (
            <Picture aspectRatioMain={'two-to-one'} {...image} />
          ) : (
            <span
              className={styles.featuredArticleCardImageFiller}
              data-testid="featured-article-image-filler"
            />
          )}
          <div className={styles.featuredArticleCardText}>
            <TypographyV2
              tag="h2"
              textStyle="sb-text-interface-heading-large"
              data-testid="featured-article-title"
            >
              <AnimationWrapper inverted={true}>
                {isLive && <span className={styles.headingLive}>LIVE: </span>}
                {title}
              </AnimationWrapper>
            </TypographyV2>
            <TypographyV2
              className={styles.featuredArticleCardStandfirst}
              textStyle="sb-text-body-default-regular"
              data-testid="featured-article-standfirst"
            >
              {standfirst}
            </TypographyV2>
            {date && (
              <div
                className={styles.featuredArticleCardDate}
                data-testid="featured-article-date"
              >
                <DateTime dateInput={date} showDateOrTime={'Date'} />
              </div>
            )}
          </div>
        </div>
      </BaseLink>
    </article>
  )
}

///////// IMPLEMENTATION /////////

export type FeaturedArticleCardProps = {
  alignLeft?: boolean
  article: {
    href: string
    title: string
    standfirst: string
    isLive?: boolean
    date?: string
    dateTime: string
    image: PictureProps | null
  }
  badge?: BadgeProps
  className?: string
  /**
   * Props to be passed through to the BaseLink component
   * E.g. for data properties for tracking purposes
   */
  linkProps?: React.ComponentPropsWithoutRef<typeof BaseLink>
}
